/**
 * `LazyPaneDocument` (1.I-2 deliverables 4 and 28): a pane's document behind the pane chunk's one
 * import (`pane-module.ts`), so the editor stays out of the root chunk, with the preview in front.
 *
 * - When `open()` prefetched the document (`PeekAnswerContext`), its answer paints the preview at
 *   once, whether or not the chunk has loaded.
 * - `PaneDocument` mounts as soon as the chunk is there, beneath the preview: hidden
 *   (`visibility: hidden`) and `inert`, so it takes no focus and no pointer. Its session opens at
 *   once; its editor is created once the preview has painted (`hold`). At the editor's first
 *   paint (or when the pane settles on a state with no editor) one commit removes the preview and
 *   shows the pane, in the same grid cell, so the first block stays where it was and the peek's
 *   scroll offset is kept.
 * - With no held answer (older than `PREFETCH_MAX_AGE_MS`, or none) there is no preview: "Opening…"
 *   until the chunk is there, then the pane as before. Back and Forward within that window
 *   preview the held answer, which can predate edits typed in the peek since; the editor
 *   replaces it at its first paint (recorded in the report's Decisions).
 */
import type { DocumentV1 } from '@codex/contracts';
import { Button } from '@codex/ui';
import { type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { PeekAnswerContext } from './nav-context.js';
import type { PaneDocumentProps } from './pane-document.js';
import { loadedPaneModule, loadPaneModule, type PaneModule } from './pane-module.js';
import { PeekPreview } from './peek-preview.js';

export type LazyPaneDocumentProps = Omit<PaneDocumentProps, 'onShown' | 'hold'>;

export function LazyPaneDocument(props: LazyPaneDocumentProps) {
  // A new document starts a new stage: its own answer, preview and swap.
  return <PaneStage key={props.docId} {...props} />;
}

function PaneStage(props: LazyPaneDocumentProps) {
  const answers = useContext(PeekAnswerContext);
  // The answer `open()` prefetched for this document, read once (the stage is keyed by it).
  const [pending] = useState(() => answers(props.docId));
  const [answer, setAnswer] = useState<DocumentV1 | null>(null);
  const [answerFailed, setAnswerFailed] = useState(false);
  const [painted, setPainted] = useState(false);
  const onPainted = useCallback(() => setPainted(true), []);
  const [loaded, setLoaded] = useState<PaneModule | null>(loadedPaneModule);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [shown, setShown] = useState(false);
  const onShown = useCallback(() => setShown(true), []);

  useEffect(() => {
    if (pending === null) return;
    let live = true;
    pending.then(
      (doc) => {
        if (live) setAnswer(doc);
      },
      // A failed answer draws no preview; the pane's own request says what failed.
      () => {
        if (live) setAnswerFailed(true);
      },
    );
    return () => {
      live = false;
    };
  }, [pending]);

  useEffect(() => {
    if (loaded !== null) return;
    // `attempt` is read so that "Try again" imports the chunk again.
    void attempt;
    let live = true;
    loadPaneModule().then(
      (module) => {
        if (live) setLoaded(module);
      },
      () => {
        if (!live) return;
        setFailed(true);
        // The failure replaces the preview; it is never drawn over it.
        setShown(true);
      },
    );
    return () => {
      live = false;
    };
  }, [loaded, attempt]);

  const previewing = answer !== null && !shown;
  // While a preview is on its way the editor waits for its paint: the editor's creation is one
  // long task, and the preview must not wait behind it.
  const hold = pending !== null && !answerFailed && !painted && !shown;
  let pane: ReactNode = null;
  if (loaded !== null) {
    pane = (
      <div
        className="cx-nav-stage__pane"
        data-hidden={previewing ? 'true' : undefined}
        inert={previewing}
      >
        <loaded.PaneDocument {...props} hold={hold} onShown={onShown} />
      </div>
    );
  } else if (failed) {
    pane = (
      <div className="cx-nav-doc__state">
        <p role="alert">This page couldn't be opened (chunk).</p>
        <Button
          variant="secondary"
          onClick={() => {
            setFailed(false);
            setAttempt((n) => n + 1);
          }}
        >
          Try again
        </Button>
      </div>
    );
  } else if (!previewing) {
    pane = (
      <p role="status" className="cx-nav-doc__quiet">
        Opening…
      </p>
    );
  }
  return (
    <div className="cx-nav-stage">
      {previewing && <PeekPreview doc={answer} onPainted={onPainted} />}
      {pane}
    </div>
  );
}
